/**
 * Settings Actions
 * NgRx actions for settings state management
 */
import { createAction, props } from '@ngrx/store';
import {
  UserProfile,
  Organization,
  TeamMember,
  NotificationPreferences,
  UpdateUserProfileRequest,
  UpdateOrganizationRequest,
  InviteTeamMemberRequest,
  UpdateTeamMemberRoleRequest,
  UpdateNotificationPreferencesRequest,
  Enable2FARequest,
  Disable2FARequest,
  TeamMembersQuery,
} from '../models/settings.models';

// User Profile Actions
export const loadUserProfile = createAction('[Settings] Load User Profile');
export const loadUserProfileSuccess = createAction(
  '[Settings] Load User Profile Success',
  props<{ profile: UserProfile }>()
);
export const loadUserProfileFailure = createAction(
  '[Settings] Load User Profile Failure',
  props<{ error: string }>()
);

export const updateUserProfile = createAction(
  '[Settings] Update User Profile',
  props<{ request: UpdateUserProfileRequest }>()
);
export const updateUserProfileSuccess = createAction(
  '[Settings] Update User Profile Success',
  props<{ profile: UserProfile }>()
);
export const updateUserProfileFailure = createAction(
  '[Settings] Update User Profile Failure',
  props<{ error: string }>()
);

export const uploadAvatar = createAction(
  '[Settings] Upload Avatar',
  props<{ file: File }>()
);
export const uploadAvatarSuccess = createAction(
  '[Settings] Upload Avatar Success',
  props<{ avatarUrl: string | null }>()
);
export const uploadAvatarFailure = createAction(
  '[Settings] Upload Avatar Failure',
  props<{ error: string }>()
);

export const requestPasswordReset = createAction('[Settings] Request Password Reset');
export const requestPasswordResetSuccess = createAction('[Settings] Request Password Reset Success');
export const requestPasswordResetFailure = createAction(
  '[Settings] Request Password Reset Failure',
  props<{ error: string }>()
);

export const enable2FA = createAction(
  '[Settings] Enable 2FA',
  props<{ request: Enable2FARequest }>()
);
export const enable2FASuccess = createAction('[Settings] Enable 2FA Success');
export const enable2FAFailure = createAction(
  '[Settings] Enable 2FA Failure',
  props<{ error: string }>()
);

export const disable2FA = createAction(
  '[Settings] Disable 2FA',
  props<{ request: Disable2FARequest }>()
);
export const disable2FASuccess = createAction('[Settings] Disable 2FA Success');
export const disable2FAFailure = createAction(
  '[Settings] Disable 2FA Failure',
  props<{ error: string }>()
);

// Organization Actions
export const loadOrganization = createAction('[Settings] Load Organization');
export const loadOrganizationSuccess = createAction(
  '[Settings] Load Organization Success',
  props<{ organization: Organization }>()
);
export const loadOrganizationFailure = createAction(
  '[Settings] Load Organization Failure',
  props<{ error: string }>()
);

export const updateOrganization = createAction(
  '[Settings] Update Organization',
  props<{ request: UpdateOrganizationRequest }>()
);
export const updateOrganizationSuccess = createAction(
  '[Settings] Update Organization Success',
  props<{ organization: Organization }>()
);
export const updateOrganizationFailure = createAction(
  '[Settings] Update Organization Failure',
  props<{ error: string }>()
);

export const uploadLogo = createAction(
  '[Settings] Upload Logo',
  props<{ file: File }>()
);
export const uploadLogoSuccess = createAction(
  '[Settings] Upload Logo Success',
  props<{ logoUrl: string }>()
);
export const uploadLogoFailure = createAction(
  '[Settings] Upload Logo Failure',
  props<{ error: string }>()
);

// Team Management Actions
export const loadTeamMembers = createAction(
  '[Settings] Load Team Members',
  props<{ query?: Partial<TeamMembersQuery> }>()
);
export const setTeamMembersFilters = createAction(
  '[Settings] Set Team Members Filters',
  props<{ search?: string; role?: string; status?: string; sort?: string }>()
);
export const setTeamMembersPage = createAction(
  '[Settings] Set Team Members Page',
  props<{ page: number; pageSize?: number }>()
);
export const loadTeamMembersSuccess = createAction(
  '[Settings] Load Team Members Success',
  props<{ members: TeamMember[]; total: number; page: number; pageSize: number }>()
);
export const loadTeamMembersFailure = createAction(
  '[Settings] Load Team Members Failure',
  props<{ error: string }>()
);

export const inviteTeamMember = createAction(
  '[Settings] Invite Team Member',
  props<{ request: InviteTeamMemberRequest }>()
);
export const inviteTeamMemberSuccess = createAction(
  '[Settings] Invite Team Member Success',
  props<{ member?: TeamMember }>()
);
export const inviteTeamMemberFailure = createAction(
  '[Settings] Invite Team Member Failure',
  props<{ error: string }>()
);

export const updateTeamMemberRole = createAction(
  '[Settings] Update Team Member Role',
  props<{ memberId: string; request: UpdateTeamMemberRoleRequest }>()
);
export const updateTeamMemberRoleSuccess = createAction(
  '[Settings] Update Team Member Role Success',
  props<{ member: TeamMember }>()
);
export const updateTeamMemberRoleFailure = createAction(
  '[Settings] Update Team Member Role Failure',
  props<{ error: string }>()
);

export const removeTeamMember = createAction(
  '[Settings] Remove Team Member',
  props<{ memberId: string }>()
);
export const removeTeamMemberSuccess = createAction(
  '[Settings] Remove Team Member Success',
  props<{ memberId: string }>()
);
export const removeTeamMemberFailure = createAction(
  '[Settings] Remove Team Member Failure',
  props<{ error: string }>()
);

export const resendInvitation = createAction(
  '[Settings] Resend Invitation',
  props<{ memberId: string }>()
);
export const resendInvitationSuccess = createAction('[Settings] Resend Invitation Success');
export const resendInvitationFailure = createAction(
  '[Settings] Resend Invitation Failure',
  props<{ error: string }>()
);

// Notification Preferences Actions
export const loadNotificationPreferences = createAction('[Settings] Load Notification Preferences');
export const loadNotificationPreferencesSuccess = createAction(
  '[Settings] Load Notification Preferences Success',
  props<{ preferences: NotificationPreferences }>()
);
export const loadNotificationPreferencesFailure = createAction(
  '[Settings] Load Notification Preferences Failure',
  props<{ error: string }>()
);

export const updateNotificationPreferences = createAction(
  '[Settings] Update Notification Preferences',
  props<{ request: UpdateNotificationPreferencesRequest }>()
);
export const updateNotificationPreferencesSuccess = createAction(
  '[Settings] Update Notification Preferences Success',
  props<{ preferences: NotificationPreferences }>()
);
export const updateNotificationPreferencesFailure = createAction(
  '[Settings] Update Notification Preferences Failure',
  props<{ error: string }>()
);

export const connectSlack = createAction(
  '[Settings] Connect Slack',
  props<{ code: string; state?: string }>()
);
export const connectSlackSuccess = createAction(
  '[Settings] Connect Slack Success',
  props<{ preferences: NotificationPreferences }>()
);
export const connectSlackFailure = createAction(
  '[Settings] Connect Slack Failure',
  props<{ error: string }>()
);

export const disconnectSlack = createAction('[Settings] Disconnect Slack');
export const disconnectSlackSuccess = createAction(
  '[Settings] Disconnect Slack Success',
  props<{ preferences: NotificationPreferences }>()
);
export const disconnectSlackFailure = createAction(
  '[Settings] Disconnect Slack Failure',
  props<{ error: string }>()
);

